import { DestroyRef, inject, Injectable } from '@angular/core';
import { map, Observable, ReplaySubject } from 'rxjs';
import { ProductSpecificCampaign } from 'src/app/core/interfaces/page-creator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BuilderService } from '@scalefast/ecommerce-core';
import { BuilderContent } from '@builder.io/sdk';

@Injectable({ providedIn: 'root' })
export class ProductSpecificCampaignsService {
  productSpecificCampaignsData$$: ReplaySubject<ProductSpecificCampaign[]> = new ReplaySubject();
  productCampaignsData$ = this.productSpecificCampaignsData$$.asObservable();

  constructor(private builderService: BuilderService) {
    this.getCampaignsData();
  }

  #destroyRef = inject(DestroyRef);

  getCampaignsData() {
    this.builderService
      .getContentsByModelName('product-specific-campaign-texts-and-codes')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((builderContent: BuilderContent[]) => {
        const currentActiveCampaigns: ProductSpecificCampaign[] = builderContent
          .map((campaignData) => {
            return {
              active: campaignData.published === 'published' ? campaignData?.data?.campaign?.active : false,
              name: campaignData?.data?.campaign?.campaignName,
              code: campaignData?.data?.campaign?.code,
              textHighlight: campaignData?.data?.campaign?.textHighlight,
              startDate: campaignData?.data?.campaign?.startDate,
              endDate: campaignData?.data?.campaign?.endDate,
              products: campaignData?.data?.campaign?.products?.split(',').map((productRef: string) => productRef.trim()),
            };
          })
          .filter(this.isValidCampaign);
        this.productSpecificCampaignsData$$.next(currentActiveCampaigns);
      });
  }

  isValidCampaign(campaign: ProductSpecificCampaign): campaign is ProductSpecificCampaign {
    const isActive = campaign?.active;
    const hasProducts = Boolean(campaign?.products?.length);
    const currentDate = new Date();
    let isValidStartDate = true;
    let isValidEndDate = true;
    if (campaign.startDate) {
      isValidStartDate = new Date(campaign.startDate) < currentDate;
    }
    if (campaign.endDate) {
      isValidEndDate = new Date(campaign.endDate) > currentDate;
    }
    return isActive && hasProducts && isValidStartDate && isValidEndDate;
  }

  getProductSpecificCampaigns(id: string, refID: string): Observable<ProductSpecificCampaign[]> {
    return this.productCampaignsData$.pipe(
      map((validCampaigns: ProductSpecificCampaign[]) =>
        validCampaigns.filter(
          (campaign) => (refID && campaign.products.includes(refID)) || (id && campaign.products.includes(id)),
        ),
      ),
    );
  }
}
